import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { useFilteredNavigation } from "@/hooks/useFilteredNavigation";
import { useAuth } from "@/contexts/AuthContext";
import { usePrefetch } from "@/hooks/usePrefetch";
import { usePlatformAdmin } from "@/hooks/usePlatformAdmin";
import { platformAdminNavItems } from "@/components/layout/navigation/navItems";
import { Separator } from "@/components/ui/separator";

type SidebarNavItemProps = {
  icon: React.ElementType;
  label: string;
  href: string;
  isActive: boolean;
};

const SidebarNavItem = ({
  icon: Icon,
  label,
  href,
  isActive,
  prefetchProps
}: SidebarNavItemProps & { prefetchProps?: any }) => (
  <Link 
    to={href} 
    className={cn(
      "flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200",
      isActive 
        ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium shadow-subtle" 
        : "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground"
    )}
    {...prefetchProps}
  >
    <div className={cn(
      "flex items-center justify-center w-6 h-6",
      isActive ? "text-sidebar-primary-foreground" : "text-sidebar-foreground/70"
    )}>
      <Icon size={18} />
    </div>
    <span className="font-medium text-sm">{label}</span>
    {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-sidebar-primary-foreground" />}
  </Link>
);

export function Sidebar() {
  const { filteredMainNavItems } = useFilteredNavigation();
  const { data: platformAdmin } = usePlatformAdmin();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { getPrefetchProps } = usePrefetch();

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      
      // Always navigate to login after signOut, even if there's an error
      // This ensures immediate redirect and prevents race conditions with AuthGuard
      if (!error) {
        navigate('/login', { replace: true });
      } else {
        // Still navigate even on error - local tokens are cleared
        navigate('/login', { replace: true });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_error', component: 'Sidebar', operation: 'sign_out' },
        extra: { message: 'Sign out error' }
      });
      // Navigate to login even on exception - fail safe
      navigate('/login', { replace: true });
    }
  };

  // Improved active state logic that handles sub-routes
  const isActiveRoute = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className={cn(
      "bg-sidebar border-r border-sidebar-border transition-all duration-300 h-screen sticky top-0 z-30 flex flex-col shadow-subtle overflow-hidden",
      isCollapsed ? "w-[68px]" : "w-[260px]"
    )}>
      <div className="flex items-center h-16 px-4 border-b border-sidebar-border justify-end">
        <button 
          type="button" 
          onClick={() => setIsCollapsed(!isCollapsed)} 
          className="p-1.5 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent 
                    transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-sidebar-ring" 
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <div className="px-3 py-3">
        {!isCollapsed && (
          <div className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">
            Menu
          </div>
        )}
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {filteredMainNavItems.map(item => {
            // Get prefetch props for this route
            const prefetchProps = getPrefetchProps(
              [item.href.replace('/', '')], 
              async () => {
                // Prefetch would happen here - placeholder for now
                return null;
              }
            );
            
            return (
            <div key={item.href} className={cn(isCollapsed && "flex justify-center")}>
              {isCollapsed ? (
                <Link 
                  to={item.href} 
                  className={cn(
                    "p-2.5 flex justify-center rounded-md transition-all duration-200 relative",
                    isActiveRoute(item.href) 
                      ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-subtle" 
                      : "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground"
                  )} 
                  aria-label={item.label}
                  {...prefetchProps}
                >
                  <item.icon size={18} />
                  {isActiveRoute(item.href) && (
                    <span className="absolute right-0.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-sidebar-primary-foreground" />
                  )}
                </Link>
              ) : (
                <SidebarNavItem 
                  icon={item.icon} 
                  label={item.label} 
                  href={item.href} 
                  isActive={isActiveRoute(item.href)}
                  prefetchProps={prefetchProps}
                />
              )}
            </div>
            );
          })}
        </nav>
      </div>

      {/* Platform Admin Navigation */}
      {platformAdmin?.isAdmin && (
        <>
          <Separator className="mx-3" />
          <div className="px-3 py-3">
            {!isCollapsed && (
              <div className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">
                Platform Admin
              </div>
            )}
            <nav className="flex-1 space-y-1">
              {platformAdminNavItems
                .filter(item => {
                  // Filter based on permissions
                  if (item.href === '/admin/audit' && !platformAdmin.permissions?.can_view_audit_logs) {
                    return false;
                  }
                  if (item.href === '/admin/settings' && !platformAdmin.permissions?.can_manage_platform) {
                    return false;
                  }
                  return true;
                })
                .map(item => {
                  const prefetchProps = getPrefetchProps(
                    [item.href.replace('/', '')], 
                    async () => null
                  );
                  
                  return (
                    <div key={item.href} className={cn(isCollapsed && "flex justify-center")}>
                      {isCollapsed ? (
                        <Link 
                          to={item.href} 
                          className={cn(
                            "p-2.5 flex justify-center rounded-md transition-all duration-200 relative",
                            isActiveRoute(item.href) 
                              ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-subtle" 
                              : "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground"
                          )} 
                          aria-label={item.label}
                          {...prefetchProps}
                        >
                          <item.icon size={18} />
                          {isActiveRoute(item.href) && (
                            <span className="absolute right-0.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-sidebar-primary-foreground" />
                          )}
                        </Link>
                      ) : (
                        <SidebarNavItem 
                          icon={item.icon} 
                          label={item.label} 
                          href={item.href} 
                          isActive={isActiveRoute(item.href)}
                          prefetchProps={prefetchProps}
                        />
                      )}
                    </div>
                  );
                })}
            </nav>
          </div>
        </>
      )}

      <div className="mt-auto">
        
        <div className={cn("border-t border-sidebar-border py-2", isCollapsed ? "px-3" : "px-3")}>
          {isCollapsed ? (
            <div className="flex justify-center">
              <button 
                onClick={handleSignOut} 
                className="p-2.5 rounded-md hover:bg-sidebar-accent transition-colors" 
                aria-label="Sign Out"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <button 
              onClick={handleSignOut} 
              className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-sidebar-accent transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center text-destructive">
                <LogOut size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">Sign Out</span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}